import { SectionEmptyState } from '../../components/ui/States';
import { useState, useSyncExternalStore } from 'react';
import { visuallyHidden } from '@mui/utils';
import { useNavigationState } from '../navigation/NavigationProvider';
import {
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { CheckIcon, WarningIcon } from '../../components/ui/icons';
import { ResponsiveDialog } from '../../components/ui/ResponsiveDialog';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { DialogContent } from '../../components/ui/DialogContent';
import { useAlerts } from './AlertsProvider';
import { StoredAlert } from './alertStore';

const empty: StoredAlert[] = [];
const subscribeEmpty = () => () => {};
const getEmpty = () => empty;
export function AlertsBadge({ userId }: { userId: string }) {
  const store = useAlerts();
  const snapshot = useSyncExternalStore(
    store?.subscribe || subscribeEmpty,
    store?.getSnapshot || getEmpty,
  );
  const entries = snapshot
    .filter((entry) => entry.userId === userId)
    .sort(
      (a, b) =>
        ({ error: 0, warning: 1, info: 2 })[a.severity] -
          { error: 0, warning: 1, info: 2 }[b.severity] ||
        b.updatedAt - a.updatedAt,
    );
  const [open, setOpen] = useNavigationState(`alerts.${userId}`, false);
  const [busy, setBusy] = useState<string>();
  const [failure, setFailure] = useState<string>();
  const label = `Alerts, ${entries.length} active`;
  if (!entries.length && !open) return null;
  return (
    <>
      <Box role="status" sx={visuallyHidden}>
        {label}
      </Box>
      <Tooltip title={label}>
        <IconButton
          aria-label={label}
          aria-haspopup="dialog"
          onClick={() => {
            setFailure(undefined);
            setOpen(true);
          }}
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <Badge
            badgeContent={entries.length}
            max={99}
            color={
              entries.some((entry) => entry.severity !== 'info')
                ? 'warning'
                : 'primary'
            }
          >
            <WarningIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <ResponsiveDialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        aria-labelledby="alerts-title"
      >
        <DialogHeader
          id="alerts-title"
          title="Alerts"
          onClose={() => setOpen(false)}
        />
        <DialogContent>
          {!entries.length && (
            <SectionEmptyState icon={CheckIcon} message="No active alerts." />
          )}
          {failure && (
            <Typography role="alert" color="error">
              {failure}
            </Typography>
          )}
          <List disablePadding>
            {entries.map((entry) => (
              <ListItem
                key={entry.key}
                divider
                sx={{ display: 'block', px: 0 }}
              >
                <Typography
                  fontWeight={700}
                  color={
                    entry.severity === 'info'
                      ? 'text.primary'
                      : `${entry.severity}.main`
                  }
                >
                  {entry.title}
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-line' }}>
                  {entry.message}
                </Typography>
                <Box
                  component="time"
                  dateTime={new Date(entry.updatedAt).toISOString()}
                  sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                >
                  {new Date(entry.updatedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Box>
                <Stack direction="row" spacing={1}>
                  {entry.action && (
                    <Button
                      disabled={entry.action.disabled || Boolean(busy)}
                      onClick={() => {
                        setFailure(undefined);
                        setBusy(entry.key);
                        void store
                          ?.run(entry.key)
                          .catch(() =>
                            setFailure(
                              'The action could not finish. Check the alert and try again.',
                            ),
                          )
                          .finally(() => setBusy(undefined));
                      }}
                    >
                      {busy === entry.key ? 'Working…' : entry.action.label}
                    </Button>
                  )}
                  {entry.dismissible && (
                    <Button onClick={() => store?.dismiss(entry.key)}>
                      Dismiss
                    </Button>
                  )}
                </Stack>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </ResponsiveDialog>
    </>
  );
}
